import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Eye, EyeOff, Plus, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

const input = "w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#C9A84C]";
const CUISINES = [
  { value: "cocktail", label: "Cocktail / Apéritifs" },
  { value: "africaine", label: "Cuisine africaine" },
  { value: "iles", label: "Cuisine des îles" },
  { value: "francaise", label: "Cuisine française" },
  { value: "mixte", label: "Mixte / Autre" },
];
const EMPTY = { title: "", subtitle: "", url: "", cuisine: "cocktail", published: true, order: 0 };

export default function AdminMenus() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/menus", { params: { include_hidden: true } }).then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, url: r.data.url }));
      toast.success("Image du menu téléversée");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); }
  };

  const add = async () => {
    if (!form.title.trim() || !form.url) { toast.error("Titre et image requis"); return; }
    setSaving(true);
    try { await api.post("/menus", { ...form, order: items.length }); setForm(EMPTY); toast.success("Menu ajouté"); load(); }
    catch { toast.error("Échec de l'ajout"); }
    finally { setSaving(false); }
  };

  const update = async (it, patch) => { try { await api.put(`/menus/${it.id}`, { ...it, ...patch }); load(); } catch { toast.error("Échec"); } };
  const remove = async (id) => { if (!window.confirm("Supprimer ce menu ?")) return; try { await api.delete(`/menus/${id}`); toast.success("Supprimé"); load(); } catch { toast.error("Échec"); } };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[idx], b = items[target];
    await api.put(`/menus/${a.id}`, { ...a, order: b.order });
    await api.put(`/menus/${b.id}`, { ...b, order: a.order });
    load();
  };

  return (
    <div data-testid="admin-menus">
      <h1 className="font-serif text-3xl text-[#2C2C2C]">Menus & cartes</h1>
      <p className="mt-1 text-sm text-[#2C2C2C]/60">Ajoutez vos cartes (images). Elles s'affichent sur la page Traiteur, de façon animée et agrandissable. Ne modifiez pas les plats existants sans validation.</p>

      <div className="mt-6 rounded-xl border border-[#2C2C2C]/10 bg-white p-6">
        <h2 className="mb-4 font-serif text-lg text-[#2C2C2C]">Ajouter une carte</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">Image de la carte (JPG/PNG, max 15 Mo)</label>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-[#2C2C2C]/30 bg-[#FAF6EF] py-6 text-sm text-[#2C2C2C]/70 hover:border-[#C9A84C]">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Téléversement…" : "Choisir une image"}
              <input type="file" accept="image/*" className="hidden" data-testid="menu-upload-input" onChange={(e) => upload(e.target.files[0])} />
            </label>
            {form.url && <img src={mediaUrl(form.url)} alt="aperçu" className="mt-3 h-40 rounded-lg object-cover" />}
          </div>
          <div className="space-y-3">
            <input className={input} placeholder="Titre (ex. Safari Grill)" data-testid="menu-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input className={input} placeholder="Sous-titre (ex. Cuisine africaine)" data-testid="menu-subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
            <select className={input} value={form.cuisine} onChange={(e) => setForm({ ...form, cuisine: e.target.value })}>
              {CUISINES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <button onClick={add} disabled={saving} data-testid="menu-add-btn" className="mt-4 flex items-center gap-2 rounded-lg bg-[#2C2C2C] px-5 py-2.5 text-sm text-white hover:bg-[#C9A84C] hover:text-[#2C2C2C] disabled:opacity-60"><Plus size={15} /> Ajouter la carte</button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <p className="text-[#2C2C2C]/50" data-testid="menus-empty">Aucune carte pour l'instant.</p>}
        {items.map((it, idx) => (
          <div key={it.id} className="overflow-hidden rounded-xl border border-[#2C2C2C]/10 bg-white" data-testid={`menu-admin-card-${it.id}`}>
            <div className="aspect-[3/4] bg-[#2C2C2C]/5"><img src={mediaUrl(it.url)} alt={it.title} className="h-full w-full object-cover" /></div>
            <div className="space-y-2 p-3 text-sm">
              <p className="truncate font-medium text-[#2C2C2C]">{it.title}</p>
              <p className="truncate text-xs text-[#2C2C2C]/50">{it.subtitle}</p>
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  <button onClick={() => move(idx, -1)} className="text-[#2C2C2C]/50 hover:text-[#2C2C2C]"><ArrowUp size={16} /></button>
                  <button onClick={() => move(idx, 1)} className="text-[#2C2C2C]/50 hover:text-[#2C2C2C]"><ArrowDown size={16} /></button>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => update(it, { published: !it.published })} data-testid={`menu-toggle-${it.id}`} className="text-[#2C2C2C]/70">{it.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                  <button onClick={() => remove(it.id)} data-testid={`menu-delete-${it.id}`} className="text-red-600"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
